import {JsonFileContent, stringifyJson} from './json';
import {isPictureFileName} from './naming';

export interface EventInfo {
    title: string;
    cover?: string;
    photographers: string[];
    events: string[];
}

export function parseEventInfo(raw: string): EventInfo | null {
    try {
        const data = JSON.parse(raw);

        if (typeof data !== 'object' || data === null || Array.isArray(data)) return null;
        if (typeof data.title !== 'string') return null;
        if (!Array.isArray(data.photographers) || !data.photographers.every((value: unknown) => typeof value === 'string')) {
            return null;
        }
        if (!Array.isArray(data.events) || !data.events.every((value: unknown) => typeof value === 'string')) {
            return null;
        }
        if (data.cover !== undefined && typeof data.cover !== 'string') return null;

        return {
            title: data.title,
            cover: data.cover,
            photographers: data.photographers,
            events: data.events,
        };
    } catch {
        return null;
    }
}

/**
 * An info.json's event info together with the content it was read from (e.g. to keep its formatting
 * when saving); `null` if it isn't valid event info. Module-level, so it can be a stable `useJsonFile` parser.
 */
export function parseEventInfoContentFile(content: JsonFileContent): { info: EventInfo; content: JsonFileContent } | null {
    const info = parseEventInfo(content.text);
    return info && {info, content};
}

export const INVALID_EVENT_INFO_MESSAGE = 'Az info.json formátuma nem megfelelő.';

export function getCoverFileName(cover: string | undefined): string | null{
    return cover?.trim().split(/[/\\]/).pop() || null;
}

/** The cover's file name if it names a picture (e.g. one a folder card can show), otherwise null. */
export function getPictureCoverFileName(cover: string | undefined): string | null {
    const coverName = getCoverFileName(cover);
    return coverName && isPictureFileName(coverName) ? coverName : null;
}

export function writeCoverFileName(cover: string): string {
    if (cover.startsWith('400/')) return cover;
    return cover.trim() ? "400/" + cover.trim() : '';
}

/**
 * Whether a file is the album cover. `cover` may be a path (e.g. `400/20260114_235218_dave.jpg`),
 * so only its last segment is compared with the file name.
 */
export function isCoverFile(cover: string | undefined, fileName: string): boolean {
    if (!cover) return false;
    const coverName = getCoverFileName(cover);
    return !!coverName && coverName === fileName;
}

/** The form of an info as it is saved: trimmed title and cover, an empty cover left out. */
export function normalizeEventInfo(info: EventInfo): EventInfo {
    return {
        title: info.title.trim(),
        cover: info.cover?.trim() || undefined,
        photographers: info.photographers,
        events: info.events,
    };
}

/** The info, as it is saved, with a picture (by file name) of the album as its cover. */
export function withCover(info: EventInfo, fileName: string): EventInfo {
    return normalizeEventInfo({...info, cover: writeCoverFileName(fileName)});
}

/** Whether two infos would save the same content (after normalization and beautification). */
export function isSameEventInfo(initial: JsonFileContent, info: EventInfo, beautify: boolean): boolean {
    return initial.beautified === beautify && stringifyJson(normalizeEventInfo(info)) === initial.text;
}

export function defaultEventInfo(title: string): EventInfo {
    return {title, photographers: [], events: []};
}
